import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Users, Crown, Heart, Baby, UserPlus, Edit, Save, X } from 'lucide-react'
import { useDemo } from '../contexts/DemoContext'
import toast from 'react-hot-toast'

const FamilyRelationshipManager = () => {
  const { family, familyMembers, setFamilyMembers } = useDemo()
  const [editingMember, setEditingMember] = useState(null)
  const [relationships, setRelationships] = useState({})

  const relationshipTypes = [
    { value: 'father', label: 'Pai', icon: Crown, color: 'text-blue-600' },
    { value: 'mother', label: 'Mãe', icon: Heart, color: 'text-pink-600' },
    { value: 'son', label: 'Filho', icon: Baby, color: 'text-green-600' },
    { value: 'daughter', label: 'Filha', icon: Baby, color: 'text-purple-600' },
    { value: 'other', label: 'Outro', icon: Users, color: 'text-gray-600' }
  ]

  useEffect(() => {
    // Inicializar relacionamentos baseados nos membros existentes
    const initialRelationships = {}
    familyMembers.forEach(member => {
      initialRelationships[member.id] = member.relationship || 'other'
    })
    setRelationships(initialRelationships)
  }, [familyMembers])

  const updateMemberRelationship = (memberId, relationship) => {
    setRelationships(prev => ({
      ...prev,
      [memberId]: relationship
    }))

    // Atualizar no contexto
    const updatedMembers = familyMembers.map(member => 
      member.id === memberId 
        ? { ...member, relationship }
        : member
    )
    setFamilyMembers(updatedMembers)
    
    toast.success('Relacionamento atualizado!')
  }

  const getRelationshipInfo = (relationshipType) => {
    return relationshipTypes.find(r => r.value === relationshipType) || relationshipTypes[4]
  }

  const getMembersByRelationship = (relationshipType) => {
    return familyMembers.filter(member => 
      relationships[member.id] === relationshipType
    )
  }

  const RelationshipCard = ({ relationship, members }) => {
    const info = getRelationshipInfo(relationship)
    const IconComponent = info.icon

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-neutral-200 p-4"
      >
        <div className="flex items-center space-x-3 mb-3">
          <div className={`p-2 rounded-lg bg-neutral-100`}>
            <IconComponent className={`h-5 w-5 ${info.color}`} />
          </div>
          <div>
            <h3 className="font-medium text-neutral-900">{info.label}</h3>
            <p className="text-sm text-neutral-500">{members.length} pessoa(s)</p>
          </div>
        </div>

        <div className="space-y-2">
          {members.map(member => (
            <div key={member.id} className="flex items-center justify-between p-2 bg-neutral-50 rounded">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {member.name?.charAt(0) || member.email?.charAt(0) || '?'}
                  </span>
                </div>
                <span className="text-sm font-medium">{member.name || member.email}</span>
              </div>
              <button
                onClick={() => setEditingMember(member.id)}
                className="p-1 hover:bg-neutral-200 rounded transition-colors"
              >
                <Edit className="h-4 w-4 text-neutral-500" />
              </button>
            </div>
          ))}
        </div>
      </motion.div>
    )
  }

  const EditRelationshipModal = ({ memberId, onClose }) => {
    const member = familyMembers.find(m => m.id === memberId)
    const currentRelationship = relationships[memberId] || 'other'

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Definir Relacionamento</h3>
            <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mb-4">
            <p className="text-sm text-neutral-600 mb-2">
              Definindo relacionamento para: <strong>{member?.name || member?.email}</strong>
            </p>
          </div>

          <div className="space-y-2">
            {relationshipTypes.map(type => {
              const IconComponent = type.icon
              return (
                <button
                  key={type.value}
                  onClick={() => {
                    updateMemberRelationship(memberId, type.value)
                    onClose()
                  }}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg border transition-colors ${
                    currentRelationship === type.value
                      ? 'border-primary-300 bg-primary-50'
                      : 'border-neutral-200 hover:bg-neutral-50'
                  }`}
                >
                  <IconComponent className={`h-5 w-5 ${type.color}`} />
                  <span className="font-medium">{type.label}</span>
                </button>
              )
            })}
          </div>
        </motion.div>
      </motion.div>
    )
  }

  if (!family) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 text-center">
        <Users className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 mb-2">Nenhuma Família Configurada</h3>
        <p className="text-neutral-600">Configure sua família primeiro para gerenciar relacionamentos.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-neutral-900 flex items-center">
              <Users className="h-6 w-6 mr-2" />
              Relacionamentos Familiares
            </h2>
            <p className="text-neutral-600 mt-1">
              Defina os relacionamentos entre os membros da família
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {relationshipTypes.map(type => {
            const members = getMembersByRelationship(type.value)
            if (members.length === 0) return null
            
            return (
              <RelationshipCard
                key={type.value}
                relationship={type.value}
                members={members}
              />
            )
          })}
        </div>

        {familyMembers.length === 0 && (
          <div className="text-center py-8">
            <UserPlus className="h-12 w-12 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 mb-2">Nenhum Membro Encontrado</h3>
            <p className="text-neutral-600">Adicione membros à família para definir relacionamentos.</p>
          </div>
        )}
      </div>

      {/* Modal de Edição */}
      {editingMember && (
        <EditRelationshipModal
          memberId={editingMember}
          onClose={() => setEditingMember(null)}
        />
      )}
    </div>
  )
}

export default FamilyRelationshipManager